import { Link } from 'react-router-dom'

import type { WorkflowDefinition } from '../../api/workflowRuns'
import { isValidWorkflowDefinitionPublicId, workflowDefinitionPublicId } from '../../api/workflowRuns'
import { resolveWorkflowDefinitionDisplay } from '../../lib/workflow/workflowDefinitionDisplay'
import { workflowDefinitionStatusLabel } from '../../lib/workflow/workflowDisplay'
import { WORKFLOW_TYPE_META, workflowTypeFromGraph } from '../../lib/workflow/workflowTemplates'
import { ChromePrimaryButton } from '../ChromeActions'
import { chromeMetaTagClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'

type Props = {
  definition: WorkflowDefinition
  angular: boolean
  running?: boolean
  onRun?: (definition: WorkflowDefinition) => void
}

export function WorkflowDefinitionCard({ definition, angular, running, onRun }: Props) {
  const display = resolveWorkflowDefinitionDisplay(definition)
  const type = workflowTypeFromGraph(definition.graph)
  const meta = WORKFLOW_TYPE_META[type]
  const nodeCount = definition.graph?.nodes?.length ?? 0
  const edgeCount = definition.graph?.edges?.length ?? 0
  const publicId = workflowDefinitionPublicId(definition)
  const builderUrl = isValidWorkflowDefinitionPublicId(publicId)
    ? `/workflows/builder/${encodeURIComponent(publicId)}`
    : '/workflows'

  return (
    <article
      className={[
        'group relative flex min-w-0 w-full max-w-full flex-col self-stretch overflow-hidden border-line bg-panel text-ink shadow-card',
        'transition-[box-shadow] duration-200 motion-reduce:transition-none',
        '[@media(hover:hover)_and_(pointer:fine)]:hover:shadow-float',
        angular ? 'rounded-none border-2' : 'rounded-theme border',
      ].join(' ')}
    >
      <Link
        to={builderUrl}
        className="flex min-h-0 w-full min-w-0 flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
        aria-label={`编排「${display.name}」`}
      >
        <div
          className={[
            'relative aspect-[16/10] w-full min-h-[7.5rem] overflow-hidden bg-surface-muted sm:min-h-[8rem]',
            angular ? '' : 'rounded-t-theme',
            'bg-[linear-gradient(145deg,color-mix(in_srgb,var(--accent)_14%,var(--surface-muted)),var(--surface-muted))]',
          ].join(' ')}
        >
          <div className="absolute inset-0 flex flex-col justify-between p-3 sm:p-3.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={chromeMetaTagClass(angular, 'outline', { hint: true })}>{meta.label}</span>
              <span className={chromeMetaTagClass(angular, definition.status === 'published' ? 'solid' : 'outline')}>
                {workflowDefinitionStatusLabel(definition.status)}
              </span>
            </div>
            <p className="text-xs font-medium text-ink-soft">
              {nodeCount} 节点 · {edgeCount} 连线
            </p>
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5 border-t border-line px-3 py-2.5 sm:px-3.5 sm:py-3">
          <ChromeTooltip content={display.name} angular={angular} side="top" align="start" className="block min-w-0">
            <h2 className="truncate text-sm font-semibold leading-snug text-ink sm:text-[15px]">
              {display.name}
            </h2>
          </ChromeTooltip>
          <p className="line-clamp-2 text-xs leading-relaxed text-ink-soft">
            {display.description || meta.description}
          </p>
        </div>
      </Link>
      {onRun ? (
        <div className="flex items-center justify-end border-t border-line px-3 py-2 sm:px-3.5">
          <ChromePrimaryButton
            density="compact"
            type="button"
            className="!h-8"
            disabled={running}
            onClick={() => onRun(definition)}
          >
            {running ? '运行中…' : '运行'}
          </ChromePrimaryButton>
        </div>
      ) : null}
    </article>
  )
}
